/**
 * Anant Trade Center (ATC) — Phase 1-4 (MVP + B2B polish + Live voice broadcast + Cinematic FOMO VFX).
 * Route: /atc  ·  Backend: /api/atc/*  +  SSE /api/atc/broadcast/stream
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import MediaLightbox, { normalizeItems } from '../../components/MediaLightbox';
import Sparkline from '../../components/Sparkline';
import CountdownTimer from '../../components/CountdownTimer';
import SalePostsGrid, { CategoryPicker } from './SalePosts';
import ShareBar from '../../components/atc/ShareBar';
import { getPrefs as sbGetPrefs } from '../../utils/soundbox';
// Wave 46 · Soundbox voice announcements are handled app-wide by useSoundboxEvents
// (mounted once in App.js) so they fire on every page — including this one — exactly
// once. Do NOT call announce()/sbAnnounce() from here too, or alerts double up.
import './atc.css';

const TABS = [
  { key: 'B2C_RETAIL',  label: '🛒 B2C Retail Floor',      subtitle: 'Public Ki Demand · 5% platform · counter or rider' },
  { key: 'B2B_BULK',    label: '🏭 B2B Wholesale Desk',    subtitle: 'ATC Bulk Hub · 3% platform · MOQ 50+ · sample-first' },
];

// ---- Global sound-mute toggle (persisted) ----
function useMuted() {
  const [muted, setMuted] = useState(() => localStorage.getItem('atc_muted') === '1');
  const toggle = () => {
    setMuted((v) => { const next = !v; localStorage.setItem('atc_muted', next ? '1' : '0'); return next; });
  };
  return { muted, toggle };
}

// ---- Zero-cost audio (Web Audio API) ----
function chime(kind = 'bid') {
  if (localStorage.getItem('atc_muted') === '1') return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (kind === 'lock' || kind === 'chaching') {
      // Cha-ching cash register: high sparkle then descending arpeggio
      [1568, 1319, 1047, 784].forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = i === 0 ? 'triangle' : 'sine';
        o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        const start = ctx.currentTime + i * 0.08;
        g.gain.setValueAtTime(0.001, start);
        g.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, start + 0.55);
        o.start(start); o.stop(start + 0.6);
      });
      // Sparkle bell on top
      const b = ctx.createOscillator(); const bg = ctx.createGain();
      b.type = 'sine'; b.frequency.value = 2093;
      b.connect(bg); bg.connect(ctx.destination);
      bg.gain.setValueAtTime(0.001, ctx.currentTime);
      bg.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.03);
      bg.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
      b.start(); b.stop(ctx.currentTime + 0.9);
    } else if (kind === 'synth') {
      // Cinematic synth-swell for tab switch — 2-note pad, ~500ms
      [329.63, 493.88].forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = 'sawtooth'; o.frequency.value = freq;
        const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 1200;
        o.connect(f); f.connect(g); g.connect(ctx.destination);
        const start = ctx.currentTime + i * 0.05;
        g.gain.setValueAtTime(0.001, start);
        g.gain.exponentialRampToValueAtTime(0.09, start + 0.15);
        g.gain.exponentialRampToValueAtTime(0.001, start + 0.55);
        o.start(start); o.stop(start + 0.6);
      });
    } else {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = kind === 'demand' ? 520 : kind === 'undercut' ? 380 : 440;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.4);
    }
  } catch (e) { /* noop */ }
}

// ---- Text-to-speech blurb (Web Speech API, zero cost) ----
function speak(text) {
  if (localStorage.getItem('atc_muted') === '1') return;
  if (sbGetPrefs().mute) return;                       // Wave 46 · respect soundbox mute
  if (!('speechSynthesis' in window)) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05; u.pitch = 1.0; u.volume = 0.7; u.lang = 'en-IN';
    window.speechSynthesis.speak(u);
  } catch (e) { /* noop */ }
}

// ---- Countdown helper ----
function fmtLeft(createdIso, hours) {
  const created = new Date(createdIso).getTime();
  const expiry = created + hours * 3600 * 1000;
  const left = Math.max(0, expiry - Date.now());
  if (left <= 0) return 'EXPIRED';
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function AnantTradeCenter({ dealType = 'B2C_RETAIL' }) {
  const { user } = useAuth() || {};
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [activeDemand, setActiveDemand] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [voiceThreadCounts, setVoiceThreadCounts] = useState({}); // demand_id -> count
  const [ticker, setTicker] = useState([]);            // running bids marquee
  const [chatStream, setChatStream] = useState([]);    // cross-demand voice replies
  const [deals, setDeals] = useState([]);              // bottom deals ticker
  const [confetti, setConfetti] = useState(null);      // {buyer,seller,amount}
  const [bidWar, setBidWar] = useState(0);             // rerender-trigger tick when 2+ bids in 30s on hot deal
  const [leaderboard, setLeaderboard] = useState([]);   // top 5 undercutters this hour
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [trending, setTrending] = useState([]);        // Wave 30 · city+category combos with >10 bids/hour
  const [broadcast, setBroadcast] = useState(false);    // Wave 30 · full-screen kiosk mode
  const [motivation, setMotivation] = useState(0);      // Wave 30 · rotating pep index
  const [dmPeer, setDmPeer] = useState(null);           // { id, username } — opens VoiceThreadModal in dm mode
  const [livePartyOpen, setLivePartyOpen] = useState(false); // Wave 37 · QR + share popover
  const [livePartyQr, setLivePartyQr] = useState('');
  const [showTour, setShowTour] = useState(false);      // Wave 37 · Onboarding Tour
  const [marketMode, setMarketMode] = useState(() => { try { return new URLSearchParams(window.location.search).get('sale') ? 'sales' : 'demands'; } catch (_) { return 'demands'; } }); // Wave 38 · 'demands' | 'sales' (Support fix 2026-09-24: ?sale= deep link)
  const bidTimesRef = useRef({});                       // demand_id -> [ts, ts, ...]
  const bidWarClearRef = useRef(null);
  const { muted, toggle: toggleMute } = useMuted();
  const lastCountRef = useRef(0);
  const undercutFlashRef = useRef({});
  const isB2B = dealType === 'B2B_BULK';
  const themeColor = isB2B ? 'amber' : 'cyan';

  const loadDemands = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/atc/demands', { params: { deal_type: dealType, status: 'OPEN', limit: 60 } });
      const rows = r.data.demands || [];
      if (lastCountRef.current > 0 && rows.length > lastCountRef.current) chime('demand');
      lastCountRef.current = rows.length;
      setDemands(rows);
    } finally { setLoading(false); }
  }, [dealType]);

  useEffect(() => { loadDemands(); }, [loadDemands]);
  useEffect(() => { api.get('/atc/settings').then((r) => setSettings(r.data)).catch(() => {}); }, []);

  // Wave 37 · Show one-time Onboarding Tour on first visit (persisted in localStorage)
  useEffect(() => {
    try {
      const done = localStorage.getItem('atc_onboarding_done') === '1';
      if (!done) {
        const t = setTimeout(() => setShowTour(true), 900);
        return () => clearTimeout(t);
      }
    } catch (_) {}
  }, []);

  // Wave 30 · trending combos ticker · Wave 32 · sound alert on new hot combo
  const prevTrendKeysRef = useRef(new Set());
  useEffect(() => {
    const load = () => api.get('/atc/trending', { params: { hours: 1, min_bids: 10, limit: 6 } })
      .then((r) => {
        const rows = r.data.trending || [];
        // Detect NEW combos vs last snapshot (avoids spamming on initial load)
        if (prevTrendKeysRef.current.size > 0) {
          const newKeys = rows
            .map((t) => `${t.city}|${t.category}`)
            .filter((k) => !prevTrendKeysRef.current.has(k));
          if (newKeys.length > 0) {
            chime('chaching');
          }
        }
        prevTrendKeysRef.current = new Set(rows.map((t) => `${t.city}|${t.category}`));
        setTrending(rows);
      })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  // Wave 30 · Broadcast Mode (F-key toggle + double-click)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        setBroadcast((v) => !v);
      } else if (e.key === 'Escape' && broadcast) {
        setBroadcast(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [broadcast]);

  // Wave 30 · Motivational rotating text during quiet moments
  useEffect(() => {
    const id = setInterval(() => setMotivation((v) => (v + 1) % 6), 4000);
    return () => clearInterval(id);
  }, []);

  // Wave 29 · hourly leaderboard rail
  const loadLeaderboard = useCallback(async () => {
    try {
      const r = await api.get('/atc/leaderboard/weekly-undercutters', { params: { limit: 5, hours: 1 } });
      setLeaderboard(r.data.top || []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);
  useEffect(() => {
    const id = setInterval(loadLeaderboard, 45000);
    return () => clearInterval(id);
  }, [loadLeaderboard]);

  // Deep-link support: /atc?demand=xxx auto-opens that demand; ?ref=<username> tracks referrer
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const did = p.get('demand');
    const ref = p.get('ref');
    if (did) {
      api.get(`/atc/demands/${did}`).then((r) => setActiveDemand(r.data.demand)).catch(() => {});
      if (ref) {
        api.post(`/atc/demands/${did}/track-referrer`, { referrer_username: ref }).catch(() => {});
      }
    }
  }, []);
  useEffect(() => {
    const id = setInterval(loadDemands, 15000);
    return () => clearInterval(id);
  }, [loadDemands]);

  // ---- Live SSE broadcast subscription (Phase 3) ----
  useEffect(() => {
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    if (!backendUrl) return;
    const es = new EventSource(`${backendUrl}/api/atc/broadcast/stream`);
    const pushNotif = (n) => setNotifications((prev) => [n, ...prev].slice(0, 12));
    const spawnEmoji = (emoji) => {
      const id = Math.random().toString(36).slice(2);
      const left = 5 + Math.random() * 90;
      setFloatingEmojis((prev) => [...prev, { id, emoji, left }]);
      setTimeout(() => setFloatingEmojis((prev) => prev.filter((e) => e.id !== id)), 3200);
    };
    es.addEventListener('demand_created', (e) => {
      const d = JSON.parse(e.data);
      const isMine = (d.deal_type === dealType);
      const msg = `New ${d.deal_type === 'B2B_BULK' ? 'B2B' : 'B2C'} demand: ${d.title}${d.city ? ' in ' + d.city : ''}. Budget ${d.budget} AT.`;
      pushNotif({ id: 'd-' + d.demand_id + Date.now(), emoji: d.urgent ? '⚡' : '📢', text: msg, kind: 'demand', ts: Date.now() });
      if (isMine) { chime('demand'); speak(msg); spawnEmoji('🚀'); loadDemands(); }
    });
    es.addEventListener('bid_placed', (e) => {
      const d = JSON.parse(e.data);
      const msg = d.is_lowest
        ? `Shockwave! @${d.merchant} undercut all bids at ${d.offered_price} AT!`
        : `@${d.merchant} placed a bid of ${d.offered_price} AT.`;
      pushNotif({ id: 'b-' + d.bid_id + Date.now(), emoji: d.is_lowest ? '💥' : '💬', text: msg, kind: 'bid', ts: Date.now() });
      // Ticker append (Wave 28 · cinematic)
      setTicker((prev) => [{
        id: 'tk-' + d.bid_id + Date.now(),
        text: `@${d.merchant} · ${d.offered_price} AT${d.is_lowest ? ' 🔥 UNDERCUT' : ''}`,
        undercut: !!d.is_lowest,
        ts: Date.now(),
      }, ...prev].slice(0, 20));
      // Bid War detector (Wave 29): 2+ bids on same demand within 30s
      const now = Date.now();
      const arr = (bidTimesRef.current[d.demand_id] || []).filter((t) => now - t < 30000);
      arr.push(now);
      bidTimesRef.current[d.demand_id] = arr;
      if (arr.length >= 2) {
        setBidWar((v) => v + 1);
        if (bidWarClearRef.current) clearTimeout(bidWarClearRef.current);
        bidWarClearRef.current = setTimeout(() => setBidWar(0), 3500);
      }
      chime(d.is_lowest ? 'undercut' : 'bid');
      if (d.is_lowest) {
        speak(msg);
        undercutFlashRef.current[d.demand_id] = Date.now();
        setDemands((prev) => [...prev]);
      }
      spawnEmoji(d.is_lowest ? '💰' : '🔥');
    });
    es.addEventListener('deal_locked', (e) => {
      const d = JSON.parse(e.data);
      const msg = `Deal locked! @${d.buyer} bought from @${d.seller} for ${d.amount} AT.`;
      pushNotif({ id: 'l-' + d.order_id + Date.now(), emoji: '🎉', text: msg, kind: 'lock', ts: Date.now() });
      // Bottom deals ticker (Wave 28)
      setDeals((prev) => [{
        id: 'dl-' + d.order_id + Date.now(),
        text: `🏆 @${d.seller} → @${d.buyer} · ${d.amount} AT`,
        ts: Date.now(),
      }, ...prev].slice(0, 12));
      // Full-screen cinematic celebration
      setConfetti({ buyer: d.buyer, seller: d.seller, amount: d.amount });
      setTimeout(() => setConfetti(null), 2600);
      chime('chaching'); speak(msg); spawnEmoji('🎉'); loadDemands(); loadLeaderboard();
    });
    es.addEventListener('voice_reply', (e) => {
      try {
        const d = JSON.parse(e.data);
        setVoiceThreadCounts((prev) => ({ ...prev, [d.demand_id]: (prev[d.demand_id] || 0) + 1 }));
        pushNotif({ id: 'v-' + d.message_id + Date.now(), emoji: '💬', text: `@${d.from_username} sent a voice reply${d.note ? ': ' + d.note : ''}`, kind: 'voice', ts: Date.now() });
        // Chat stream feed (Wave 28)
        setChatStream((prev) => [{
          id: 'cs-' + d.message_id + Date.now(),
          demand_id: d.demand_id,
          role: d.role,
          username: d.from_username,
          text: d.transcript || d.note || '(voice)',
          audio_url: d.audio_url,
          ts: Date.now(),
        }, ...prev].slice(0, 25));
        chime('bid'); spawnEmoji('💬');
      } catch (err) { /* noop */ }
    });
    es.onerror = () => { /* browser will auto-retry */ };
    return () => es.close();
  }, [loadDemands, dealType]);

  // Hot deal: pick the demand with the most bids as the "spotlight" (Wave 28)
  const hotDeal = demands.reduce((best, d) => (!best || (d.bid_count || 0) > (best.bid_count || 0)) ? d : best, null);
  const otherDemands = hotDeal ? demands.filter((d) => d.id !== hotDeal.id) : demands;

  return (
    <div className={`atc-floor atc-theme-${themeColor} ${broadcast ? 'atc-broadcast' : ''} min-h-screen ${broadcast ? 'pt-4' : 'pt-28 sm:pt-32'} pb-20 px-2 sm:px-4 bg-slate-950 text-white relative overflow-hidden`}
         data-testid="atc-page"
         onDoubleClick={(e) => {
           // Double-click on empty background toggles broadcast
           if (e.target === e.currentTarget) setBroadcast((v) => !v);
         }}>
      {/* Neon grid + scanlines + ambient particles */}
      <div className="atc-neon-grid" aria-hidden="true" />
      <div className="atc-scanlines" aria-hidden="true" />
      <div className="atc-particles pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i} className="atc-particle" style={{ left: `${(i * 4.7) % 100}%`, animationDelay: `${(i % 8) * 1.3}s`, animationDuration: `${18 + (i % 5) * 3}s` }} />
        ))}
      </div>

      {/* Floating emoji reactions */}
      <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
        {floatingEmojis.map((e) => (
          <div key={e.id} className="atc-float-emoji" style={{ left: `${e.left}%` }}>{e.emoji}</div>
        ))}
      </div>

      {/* Full-screen DEAL LOCKED cinematic overlay */}
      {confetti && (
        <div className="atc-confetti-overlay" data-testid="atc-confetti-overlay">
          <div className="atc-confetti-burst">
            {Array.from({ length: 60 }).map((_, i) => (
              <span key={i} className="atc-confetti-piece" style={{
                left: `${Math.random() * 100}%`,
                background: ['#f59e0b','#10b981','#22d3ee','#f43f5e','#a78bfa'][i % 5],
                animationDelay: `${(i % 20) * 40}ms`,
              }} />
            ))}
          </div>
          <div className="atc-confetti-title">🎉 DEAL LOCKED 🎉</div>
          <div className="atc-confetti-sub">
            @{confetti.seller} → @{confetti.buyer} · <b>{confetti.amount} AT</b>
          </div>
        </div>
      )}

      {/* ─── Command bar ─── */}
      <div className="max-w-7xl mx-auto relative z-10 flex items-center gap-2 flex-wrap mb-3">
        <div className="atc-live-badge">
          <span className="atc-live-dot" /> LIVE · Trade Floor
        </div>
        <div className={`atc-mode-chip atc-mode-${themeColor}`} data-testid="atc-mode-chip">
          {isB2B ? '🏭 B2B Bulk' : '🏬 B2C Retail'}
        </div>
        <div className="hidden sm:block text-[10px] opacity-70">
          {settings ? `Anti-spam ${settings.anti_spam_hold_pct}% · commission ${isB2B ? settings.b2b_commission_pct : settings.b2c_commission_pct}%${isB2B ? ` · MOQ ${settings.b2b_min_moq || 50}+` : ''}` : ''}
        </div>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {/* Cross-mode switch */}
          <div className="atc-tab-swap" data-testid="atc-tab-swap">
            <a href="/atc/b2c" onClick={() => chime('synth')} className={`atc-tab ${!isB2B ? 'atc-tab-active atc-tab-b2c' : ''}`} data-testid="atc-tab-b2c">B2C</a>
            <a href="/atc/b2b" onClick={() => chime('synth')} className={`atc-tab ${isB2B ? 'atc-tab-active atc-tab-b2b' : ''}`} data-testid="atc-tab-b2b">B2B</a>
          </div>
          <KingBadge />
          <NotificationBell />
          <button onClick={() => setBroadcast((v) => !v)}
                  data-testid="atc-broadcast-toggle"
                  title={broadcast ? 'Exit Broadcast (F or Esc)' : 'Broadcast Mode (F key)'}
                  className={`atc-icon-btn ${broadcast ? 'ring-2 ring-fuchsia-400' : ''}`}>
            {broadcast ? '⛶' : '🎥'}
          </button>
          {broadcast && (
            <button onClick={async () => {
              setLivePartyOpen(true);
              chime('chaching');
              if (!livePartyQr) {
                try {
                  const url = window.location.href;
                  const r = await api.get('/atc/live-party/qr', { params: { url, size: 320 } });
                  setLivePartyQr(r.data?.image || '');
                } catch (_) { /* silent */ }
              }
            }}
            data-testid="atc-live-party-share"
            title="Share live-link · QR · WhatsApp"
            className="atc-cta-post atc-live-party-btn">
              🎥 Live Party · Share
            </button>
          )}
          <button onClick={toggleMute} data-testid="atc-mute-toggle"
                  className={`atc-icon-btn ${muted ? 'opacity-60' : ''}`} title={muted ? 'Sound off' : 'Sound on'}>
            {muted ? '🔇' : '🔊'}
          </button>
          <button onClick={() => setShowPost(true)} data-testid="atc-post-cta"
                  className="atc-cta-post">
            ➕ Post Demand
          </button>
        </div>
      </div>

      {/* ─── Wave 30 · Trending Now marquee (3rd ticker) ─── */}
      <div className="max-w-7xl mx-auto relative z-10 mb-2" data-testid="atc-trending-ticker">
        <div className="atc-ticker atc-ticker-trending">
          <div className="atc-ticker-label atc-ticker-label-trending">🔥 TRENDING NOW</div>
          <div className="atc-ticker-track atc-ticker-track-trending">
            {trending.length === 0 ? (
              <span className="atc-ticker-item opacity-60">
                {[
                  'Kaam karo, hasi aayegi 🎯',
                  'Bharat ka apna trade floor 🇮🇳',
                  'Har bid ek kadam aage 🚀',
                  'Ek deal, aneko sapne 💫',
                  'Kya rate final? Bolo aur jeeto 🏆',
                  'Universal Boom pending... 💥',
                ][motivation]}
              </span>
            ) : (
              <>
                {trending.map((t, i) => (
                  <span key={`${t.city}-${t.category}-${i}`} className="atc-ticker-item atc-ticker-trending-item">
                    ▸ {t.city.toUpperCase()} · {t.category.toUpperCase()} · <b>{t.bids} bids/hr</b>
                    {t.b2b > 0 && <span className="opacity-60"> (B2B×{t.b2b})</span>}
                  </span>
                ))}
                {trending.map((t, i) => (
                  <span key={`${t.city}-${t.category}-${i}-dup`} className="atc-ticker-item atc-ticker-trending-item">
                    ▸ {t.city.toUpperCase()} · {t.category.toUpperCase()} · <b>{t.bids} bids/hr</b>
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Bids marquee ticker ─── */}
      <div className="max-w-7xl mx-auto relative z-10 mb-3" data-testid="atc-bids-ticker">
        <div className="atc-ticker">
          <div className="atc-ticker-label">💹 LIVE BIDS</div>
          <div className="atc-ticker-track">
            {ticker.length === 0 ? (
              <span className="atc-ticker-item opacity-50">Waiting for the first bid…</span>
            ) : (
              <>
                {ticker.slice(0, 8).map((t) => (
                  <span key={t.id} className={`atc-ticker-item ${t.undercut ? 'atc-ticker-hot' : ''}`}>
                    ▸ {t.text}
                  </span>
                ))}
                {/* Duplicate for seamless scroll */}
                {ticker.slice(0, 8).map((t) => (
                  <span key={t.id + '-dup'} className={`atc-ticker-item ${t.undercut ? 'atc-ticker-hot' : ''}`}>
                    ▸ {t.text}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Merchant scorecard (secondary strip) ─── */}
      <div className="max-w-7xl mx-auto relative z-10">
        <MerchantScorecard />
      </div>

      {/* ─── Wave 38 · Market Mode Toggle (Demands vs Sales) ─── */}
      <div className="max-w-7xl mx-auto relative z-10 mt-3 mb-2 flex justify-center" data-testid="atc-market-toggle">
        <div className="atc-seg-toggle">
          <button onClick={() => setMarketMode('demands')} data-testid="atc-seg-demands"
                  className={`atc-seg-btn ${marketMode === 'demands' ? `atc-seg-btn-active ${isB2B ? 'b2b' : ''}` : ''}`}>
            🛒 Buyer Demands
          </button>
          <button onClick={() => setMarketMode('sales')} data-testid="atc-seg-sales"
                  className={`atc-seg-btn ${marketMode === 'sales' ? `atc-seg-btn-active ${isB2B ? 'b2b' : ''}` : ''}`}>
            🏷️ Sellers Bazaar
          </button>
        </div>
      </div>

      {/* ─── 3-column trading floor (Demands mode only) ─── */}
      {marketMode === 'demands' && (
      <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* LEFT · New Demand Stream */}
        <section className="lg:col-span-3 atc-panel" data-testid="atc-panel-demands">
          <div className="atc-panel-title">
            <span>🆕 New Demand Stream</span>
            <span className="opacity-60 text-[10px]">{demands.length} open</span>
          </div>
          <div className="atc-panel-body space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {loading && <div className="text-[11px] opacity-60 text-center py-6">⏳ Loading…</div>}
            {!loading && demands.length === 0 && (
              <div className="text-[11px] opacity-60 text-center py-6">
                No open demands in {isB2B ? 'B2B' : 'B2C'} — be the first! 🚀
              </div>
            )}
            {otherDemands.map((d) => (
              <div key={d.id} className="atc-slide-in">
                <DemandCard demand={d} onOpen={() => setActiveDemand(d)}
                            threadCount={voiceThreadCounts[d.id] || 0}
                            shockwave={undercutFlashRef.current[d.id] && Date.now() - undercutFlashRef.current[d.id] < 3000} />
              </div>
            ))}
          </div>
        </section>

        {/* CENTER · Hot Deal Spotlight */}
        <section className={`lg:col-span-6 atc-panel atc-panel-spotlight ${bidWar ? 'atc-bidwar' : ''}`} data-testid="atc-panel-hot">
          {bidWar > 0 && <div className="atc-camera-flash" data-testid="atc-bidwar-flash" key={bidWar} />}
          <div className="atc-panel-title">
            <span>🎯 Hot Deal · Spotlight {bidWar > 0 && <span className="atc-bidwar-tag" data-testid="atc-bidwar-tag">⚔️ BID WAR</span>}</span>
            {hotDeal && <span className={`atc-hot-count ${bidWar ? 'atc-count-shake' : ''}`} data-testid="atc-bidwar-counter">🔥 {hotDeal.bid_count || 0} bids</span>}
          </div>
          <div className="atc-spotlight-body">
            {hotDeal ? (
              <div className="atc-spotlight-card">
                <div className="atc-spotlight-glow" />
                <DemandCard demand={hotDeal} onOpen={() => setActiveDemand(hotDeal)}
                            threadCount={voiceThreadCounts[hotDeal.id] || 0}
                            shockwave={undercutFlashRef.current[hotDeal.id] && Date.now() - undercutFlashRef.current[hotDeal.id] < 3000} />
                <button onClick={() => setActiveDemand(hotDeal)} data-testid="atc-spotlight-open"
                        className="atc-spotlight-cta">
                  🚀 Enter Bid War →
                </button>
              </div>
            ) : (
              <div className="atc-spotlight-empty">
                <div className="text-3xl mb-2">🕹️</div>
                <div className="font-black text-lg">Trading floor is quiet</div>
                <div className="text-[11px] opacity-70 mt-1">Post the first demand to light up the floor.</div>
              </div>
            )}
          </div>
        </section>

        {/* RIGHT · Chat Stream */}
        <section className="lg:col-span-3 atc-panel" data-testid="atc-panel-chat">
          <div className="atc-panel-title">
            <span>💬 Chat Stream</span>
            <span className="opacity-60 text-[10px]">{chatStream.length} live</span>
          </div>
          <div className="atc-panel-body space-y-1.5 max-h-[70vh] overflow-y-auto pr-1">
            {chatStream.length === 0 && (
              <div className="text-[11px] opacity-60 text-center py-6">
                No voice replies yet. Open any demand → 💬 Voice Chat
              </div>
            )}
            {chatStream.map((c) => (
              <button key={c.id}
                      onClick={() => {
                        const d = demands.find((x) => x.id === c.demand_id);
                        if (d) setActiveDemand(d);
                      }}
                      onMouseEnter={(e) => {
                        // Wave 30 · hands-free auto-play voice on hover
                        if (!c.audio_url) return;
                        try {
                          if (window.__atcHoverAudio) { window.__atcHoverAudio.pause(); }
                          const a = new Audio(c.audio_url);
                          a.volume = 0.7;
                          window.__atcHoverAudio = a;
                          a.play().catch(() => {});
                          e.currentTarget.classList.add('atc-chat-playing');
                        } catch {}
                      }}
                      onMouseLeave={(e) => {
                        try {
                          if (window.__atcHoverAudio) { window.__atcHoverAudio.pause(); window.__atcHoverAudio = null; }
                          e.currentTarget.classList.remove('atc-chat-playing');
                        } catch {}
                      }}
                      data-testid={`atc-chat-stream-${c.id}`}
                      className="atc-chat-row w-full text-left">
                <div className="flex items-center gap-1.5 text-[9px] font-black opacity-80">
                  <span className={c.role === 'buyer' ? 'text-amber-300' : 'text-cyan-300'}>
                    {c.role === 'buyer' ? '🛒 buyer' : '🏪 merchant'}
                  </span>
                  <span>@{c.username}</span>
                  {c.audio_url && <span className="ml-auto opacity-60">🎧 hover to play</span>}
                </div>
                <div className="text-[11px] mt-0.5 line-clamp-2">{c.text}</div>
                {c.audio_url && (
                  <div className="atc-mini-waveform mt-1" aria-hidden="true">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <span key={i} className="atc-wave-bar" style={{ animationDelay: `${(i * 60) % 800}ms` }} />
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      </div>
      )}

      {/* ─── Wave 38 · Sale Posts grid (Sales mode) ─── */}
      {marketMode === 'sales' && (
        <SalePostsGrid dealType={dealType} currentUser={user} />
      )}

      {/* ─── Bottom deals ticker ─── */}
      <div className="max-w-7xl mx-auto relative z-10 mt-3" data-testid="atc-deals-ticker">
        <div className="atc-ticker atc-ticker-deals">
          <div className="atc-ticker-label atc-ticker-label-deals">🏆 DEALS LOCKED</div>
          <div className="atc-ticker-track atc-ticker-track-deals">
            {deals.length === 0 ? (
              <span className="atc-ticker-item opacity-50">No deals locked yet in this session.</span>
            ) : (
              <>
                {deals.map((t) => (<span key={t.id} className="atc-ticker-item atc-ticker-deal">▸ {t.text}</span>))}
                {deals.map((t) => (<span key={t.id + '-dup'} className="atc-ticker-item atc-ticker-deal">▸ {t.text}</span>))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Notifications (mini-drawer, still preserved) */}
      {notifications.length > 0 && (
        <details className="max-w-7xl mx-auto relative z-10 mt-3 rounded-xl bg-slate-900/70 border border-cyan-400/30" data-testid="atc-notif-feed">
          <summary className="cursor-pointer text-[10px] font-black text-cyan-300 uppercase px-3 py-2 list-none">
            📡 Live Broadcast · {notifications.length} events
          </summary>
          <div className="px-3 pb-2 max-h-40 overflow-y-auto">
            {notifications.map((n) => (
              <div key={n.id} className="flex items-center gap-2 py-1 border-b border-slate-800 last:border-0" data-testid={`atc-notif-${n.kind}`}>
                <span className="text-lg">{n.emoji}</span>
                <span className="text-[11px] flex-1">{n.text}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {showPost && <PostDemandModal dealType={dealType} settings={settings} onClose={() => setShowPost(false)} onPosted={() => { setShowPost(false); loadDemands(); }} />}
      {activeDemand && (
        <DemandDetailModal
          demandId={activeDemand.id}
          me={user}
          onClose={() => setActiveDemand(null)}
          onChanged={() => { loadDemands(); setActiveDemand(null); }}
        />
      )}

      {/* Wave 29 · Merchant Leaderboard slide-in rail (desktop only) */}
      <aside className={`atc-leaderboard-rail ${leaderboardOpen ? 'atc-rail-open' : 'atc-rail-closed'}`}
             data-testid="atc-leaderboard-rail">
        <button onClick={() => setLeaderboardOpen((v) => !v)}
                data-testid="atc-leaderboard-toggle"
                className="atc-rail-toggle" title={leaderboardOpen ? 'Collapse' : 'Expand'}>
          {leaderboardOpen ? '⟩' : '⟨ 🏆'}
        </button>
        <div className="atc-rail-header">
          <span>🏆 Top Undercutters</span>
          <span className="atc-rail-sub">this hour</span>
        </div>
        <div className="atc-rail-body">
          {leaderboard.length === 0 && (
            <div className="atc-rail-empty">
              No lowest-bid wins in the last hour.<br/>
              <span className="opacity-70">Bid aggressively to claim #1! 🥇</span>
            </div>
          )}
          {leaderboard.map((m, i) => {
            // Wave 36 · faux 7-point trend seeded from win count for visual polish
            const trend = Array.from({ length: 7 }).map((_, k) =>
              Math.max(0, Math.round((m.lowest_bids || 0) * (0.5 + Math.sin(k + i) * 0.35 + k * 0.06))));
            return (
              <div key={m.merchant_id} className="atc-rail-row" data-testid={`atc-leaderboard-${i+1}`}
                   style={{ animationDelay: `${i * 60}ms` }}>
                <div className={`atc-rail-rank atc-rank-${i+1}`}>
                  {['🥇','🥈','🥉','4','5'][i] || (i + 1)}
                </div>
                <div className="atc-rail-name">
                  <div className="text-xs font-black truncate">@{m.username || 'unknown'}</div>
                  <div className="text-[10px] opacity-70 flex items-center gap-1">
                    <span>🔥 {m.lowest_bids} · 🏆 {m.wins}</span>
                    <Sparkline data={trend} width={40} height={12} color="#fbbf24" showArea />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="atc-rail-footer">
          🔄 refresh: 45s · live
        </div>
      </aside>

      {/* Wave 37 · Live Party Share Popover (QR + WA + Copy) */}
      {livePartyOpen && (
        <LivePartyShareModal
          url={typeof window !== 'undefined' ? window.location.href : ''}
          qr={livePartyQr}
          onClose={() => setLivePartyOpen(false)}
        />
      )}

      {/* Wave 37 · One-time Onboarding Tour (first visit) */}
      {showTour && <OnboardingTour onDone={() => { setShowTour(false); try { localStorage.setItem('atc_onboarding_done', '1'); } catch (_) {} }} />}
    </div>
  );
}

// ============ Demand Card ============
function DemandCard({ demand, onOpen, shockwave, threadCount }) {
  const { user } = useAuth() || {};
  const [, force] = useState(0);
  const [showQuickVoice, setShowQuickVoice] = useState(false);
  const [showThread, setShowThread] = useState(false);
  useEffect(() => {
    const id = setInterval(() => force((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const pinned = demand.is_pinned_urgent;
  const left = fmtLeft(demand.created_at, demand.expiry_hours);
  const expired = left === 'EXPIRED';
  // Wave 38 · derive expires_at for CountdownTimer if backend didn't send it
  const expiresAt = demand.expires_at || (() => {
    try {
      const created = new Date(demand.created_at);
      return new Date(created.getTime() + (demand.expiry_hours || 12) * 3600 * 1000).toISOString();
    } catch (_) { return null; }
  })();
  const canQuickBid = user?.id && user.id !== demand.buyer_id;
  const canJoinThread = !!user?.id;
  return (
    <div className={`relative overflow-hidden rounded-2xl border-2 p-3 bg-slate-900/70 hover:border-purple-300 transition space-y-1.5 ${
              pinned ? 'border-amber-400 shadow-lg shadow-amber-500/30 atc-pulse-urgent' : 'border-slate-700'
            }`}>
      {shockwave && <div className="atc-shockwave" data-testid={`atc-shockwave-${demand.id}`} />}
      <button onClick={onOpen} data-testid={`atc-demand-card-${demand.id}`} className="text-left w-full space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-black truncate flex-1">{demand.title}</div>
          {pinned && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-black">⚡ URGENT</span>}
        </div>
        {demand.description && <div className="text-[11px] opacity-80 line-clamp-2">{demand.description}</div>}
        <div className="flex items-center gap-2 flex-wrap text-[10px]">
          <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-black">💰 {demand.target_budget_at} AT</span>
          <span className="px-1.5 py-0.5 rounded bg-slate-800 opacity-80">📦 Qty {demand.quantity}</span>
          {demand.city && <span className="px-1.5 py-0.5 rounded bg-slate-800 opacity-80">📍 {demand.city}</span>}
          <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-black">🔥 {demand.bid_count} bid{demand.bid_count !== 1 ? 's' : ''}</span>
          {demand.deal_type === 'B2B_BULK' && demand.sample_required && (
            <span className="px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-200 font-black">🧪 Sample-first</span>
          )}
        </div>
        <div className={`text-[9px] font-black ${expired ? 'text-rose-300' : 'text-slate-400'} flex items-center gap-1.5`}>
          By @{demand.buyer_username} ·
          <CountdownTimer expiresAt={expiresAt} compact />
        </div>
      </button>
      <div className="flex items-center gap-1.5">
        {canQuickBid && !expired && (
          <button onClick={() => setShowQuickVoice(true)} data-testid={`atc-voice-reply-${demand.id}`}
                  className="flex-1 mt-1 px-2 py-1 rounded bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-400 hover:to-pink-500 text-white text-[10px] font-black flex items-center justify-center gap-1">
            🎤 1-tap Voice Bid
          </button>
        )}
        {canJoinThread && (
          <button onClick={() => setShowThread(true)} data-testid={`atc-voice-thread-${demand.id}`}
                  className="relative flex-1 mt-1 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 border border-cyan-400/40 text-cyan-300 text-[10px] font-black flex items-center justify-center gap-1">
            💬 Voice Chat
            {threadCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-cyan-500 text-slate-900 text-[9px] font-black" data-testid={`atc-voice-thread-count-${demand.id}`}>
                {threadCount}
              </span>
            )}
          </button>
        )}
      </div>
      {showQuickVoice && (
        <PlaceBidModal demandId={demand.id} demand={demand} autoRecord
                       onClose={() => setShowQuickVoice(false)}
                       onPlaced={() => setShowQuickVoice(false)} />
      )}
      {showThread && (
        <VoiceThreadModal demand={demand} onClose={() => setShowThread(false)} />
      )}
    </div>
  );
}

// ============ Post Demand Modal ============
function PostDemandModal({ dealType, settings, onClose, onPosted }) {
  const isB2B = dealType === 'B2B_BULK';
  const moqMin = settings?.b2b_min_moq || 50;
  const [form, setForm] = useState({
    title: '', description: '', category: '', quantity: isB2B ? moqMin : 1,
    target_budget_at: isB2B ? 5000 : 100, city: '',
    expiry_hours: 12, fulfillment_mode: 'COUNTER_PICKUP', bidding_mode: 'OPEN', is_pinned_urgent: false,
    audio_note_url: null, sample_required: isB2B,
    media_urls: [],
  });
  const [busy, setBusy] = useState(false);
  const [recStatus, setRecStatus] = useState('idle');
  const [uploading, setUploading] = useState(null);
  const recognitionRef = useRef(null);
  const audioMRRef = useRef(null);
  const audioChunksRef = useRef([]);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const commPct = isB2B ? (settings?.b2b_commission_pct || 3) : (settings?.b2c_commission_pct || 5);
  const holdPct = settings?.anti_spam_hold_pct || 10;
  const holdAmt = Math.round((form.target_budget_at || 0) * holdPct / 100);
  const estCommission = Math.round((form.target_budget_at || 0) * commPct / 100);

  const startVoice = () => {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) { toast.error('Voice not supported in this browser'); return; }
    const r = new SpeechRec();
    r.lang = 'en-IN'; r.continuous = false; r.interimResults = false;
    r.onstart = () => setRecStatus('listening');
    r.onerror = () => { setRecStatus('idle'); toast.error('Voice recognition failed'); };
    r.onend = () => setRecStatus('idle');
    r.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setForm((p) => ({ ...p, description: (p.description ? p.description + ' ' : '') + text }));
      toast.success('🎤 Transcribed');
    };
    recognitionRef.current = r; r.start();
    setTimeout(() => { try { r.stop(); } catch (e) {} }, 15000);
  };

  // 15-sec audio-note recorder via MediaRecorder → uploads to /webshop/upload/audio
  const toggleAudioNote = async () => {
    if (audioMRRef.current && audioMRRef.current.state === 'recording') {
      audioMRRef.current.stop(); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); audioMRRef.current = mr; audioChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecStatus('idle');
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setUploading('audio');
        try {
          const fd = new FormData(); fd.append('file', blob, 'demand-note.webm');
          const r = await api.post('/webshop/upload/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setForm((p) => ({ ...p, audio_note_url: r.data.url }));
          toast.success('🎤 Voice note attached');
        } catch (e) { toast.error('Audio upload failed'); }
        finally { setUploading(null); }
      };
      mr.start(); setRecStatus('audio-recording');
      setTimeout(() => { try { mr.state === 'recording' && mr.stop(); } catch (e) {} }, 15000);
    } catch (e) { toast.error('Mic access denied'); }
  };

  // Client-side compression: resize down to 1280px and re-encode at 0.8 JPEG quality → keeps <1MB.
  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        let { width, height } = img;
        if (width > max || height > max) {
          const scale = max / Math.max(width, height);
          width = Math.round(width * scale); height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('encode failed')), 'image/jpeg', 0.82);
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading('photo');
    try {
      for (const f of files.slice(0, 5)) {
        const blob = await compressImage(f);
        const fd = new FormData(); fd.append('file', blob, (f.name || 'photo') + '.jpg');
        const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm((p) => ({ ...p, media_urls: [...(p.media_urls || []), r.data.url] }));
      }
      toast.success('📸 Photos attached');
    } catch (err) { toast.error('Photo upload failed'); }
    finally { setUploading(null); if (photoInputRef.current) photoInputRef.current.value = ''; }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error('Video too big (max 20 MB)'); return; }
    setUploading('video');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((p) => ({ ...p, media_urls: [...(p.media_urls || []), r.data.url] }));
      toast.success('🎥 Video attached');
    } catch (err) { toast.error('Video upload failed'); }
    finally { setUploading(null); if (videoInputRef.current) videoInputRef.current.value = ''; }
  };

  const removeMedia = (url) => setForm((p) => ({ ...p, media_urls: (p.media_urls || []).filter((u) => u !== url) }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (form.target_budget_at <= 0) { toast.error('Budget must be > 0'); return; }
    if (isB2B && form.quantity < moqMin) { toast.error(`B2B needs MOQ ≥ ${moqMin} pcs`); return; }
    setBusy(true);
    try {
      const r = await api.post('/atc/demands', { ...form, deal_type: dealType });
      toast.success(`✅ Posted! ${r.data.demand.commitment_hold_at} AT locked as commitment.`);
      onPosted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="atc-post-modal"
           className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-emerald-400/60 p-4 max-h-[92vh] overflow-y-auto space-y-2">
        <div className="text-lg font-black">➕ Post Demand · {isB2B ? 'B2B Wholesale' : 'B2C Retail'}</div>
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/40 p-2 text-[11px] space-y-0.5" data-testid="atc-post-cost-preview">
          <div>💰 Platform commission: <b className="text-emerald-300">{commPct}%</b> (~{estCommission} AT on {form.target_budget_at || 0} AT deal)</div>
          <div>🔒 Anti-spam lock: <b className="text-amber-300">{holdPct}% = {holdAmt} AT</b> (refunded on pre-accept cancel)</div>
          {isB2B && <div className="text-purple-300">🏭 MOQ enforced: min <b>{moqMin} pcs</b></div>}
        </div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What do you need? *"
               data-testid="atc-post-title" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="relative">
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="More details (or tap 🎤 to speak)…" rows={2}
                    data-testid="atc-post-desc" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <button onClick={startVoice} disabled={recStatus !== 'idle'} data-testid="atc-post-voice"
                  className={`absolute bottom-2 right-2 px-2 py-1 rounded text-[10px] font-black ${recStatus === 'listening' ? 'bg-rose-500 animate-pulse text-white' : 'bg-emerald-500 text-slate-900'}`}>
            {recStatus === 'listening' ? '🎤 Listening…' : '🎤 Voice→Text'}
          </button>
        </div>

        {/* Media uploaders (Wave 25 fix) */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploading === 'photo'}
                  data-testid="atc-post-photo-btn"
                  className="px-2.5 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-black">
            {uploading === 'photo' ? '⏳ Photo…' : '📸 Add Photos'}
          </button>
          <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" data-testid="atc-post-photo-input" />
          <button type="button" onClick={() => videoInputRef.current?.click()} disabled={uploading === 'video'}
                  data-testid="atc-post-video-btn"
                  className="px-2.5 py-1.5 rounded bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-black">
            {uploading === 'video' ? '⏳ Video…' : '🎥 Add Video'}
          </button>
          <input ref={videoInputRef} type="file" accept="video/*" onChange={uploadVideo} className="hidden" data-testid="atc-post-video-input" />
          <button type="button" onClick={toggleAudioNote} disabled={uploading === 'audio'}
                  data-testid="atc-post-audio-btn"
                  className={`px-2.5 py-1.5 rounded text-[10px] font-black ${recStatus === 'audio-recording' ? 'bg-rose-500 text-white animate-pulse' : form.audio_note_url ? 'bg-emerald-500 text-slate-900' : 'bg-amber-500 text-slate-900'}`}>
            {recStatus === 'audio-recording' ? '⏹ Stop' : form.audio_note_url ? '🎤 Voice Note ✓' : '🎤 15s Voice Note'}
          </button>
        </div>
        {form.media_urls && form.media_urls.length > 0 && (
          <div className="flex gap-1.5 flex-wrap" data-testid="atc-post-media-thumbs">
            {form.media_urls.map((u) => {
              const isVideo = u.match(/\.(mp4|mov|webm|avi)/i);
              return (
                <div key={u} className="relative w-16 h-16 rounded overflow-hidden bg-slate-800 border border-slate-700">
                  {isVideo ? <video src={u} className="w-full h-full object-cover" /> : <img src={u} alt="" className="w-full h-full object-cover" />}
                  <button onClick={() => removeMedia(u)} className="absolute top-0 right-0 w-4 h-4 rounded-bl bg-rose-500 text-white text-[8px] font-black">✕</button>
                </div>
              );
            })}
          </div>
        )}
        {form.audio_note_url && (
          <div className="flex items-center gap-2">
            <audio src={form.audio_note_url} controls className="h-8 flex-1" />
            <button onClick={() => setForm({ ...form, audio_note_url: null })} className="text-rose-300 text-[10px] font-black">Remove</button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <CategoryPicker value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="atc-demand-cat" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City"
                 className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input type="number" min={isB2B ? moqMin : 1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: +e.target.value })}
                 placeholder={isB2B ? `Qty (min ${moqMin})` : 'Qty'} data-testid="atc-post-qty"
                 className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input type="number" min={1} value={form.target_budget_at} onChange={(e) => setForm({ ...form, target_budget_at: +e.target.value })}
                 placeholder="Budget (AT) *" data-testid="atc-post-budget" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        </div>
        {isB2B && (
          <label className="flex items-center gap-2 text-xs font-black text-purple-300 bg-purple-500/10 border border-purple-400/40 rounded-lg px-2 py-1.5">
            <input type="checkbox" checked={form.sample_required} onChange={(e) => setForm({ ...form, sample_required: e.target.checked })} data-testid="atc-post-sample" />
            🧪 Sample-First required (merchant must send a sample before bulk deal locks)
          </label>
        )}
        <div className="grid grid-cols-2 gap-2">
          <select value={form.fulfillment_mode} onChange={(e) => setForm({ ...form, fulfillment_mode: e.target.value })}
                  className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs">
            <option value="COUNTER_PICKUP">🏪 Counter Pickup</option>
            <option value="RIDER">🛵 Rider (+30 AT)</option>
          </select>
          <select value={form.expiry_hours} onChange={(e) => setForm({ ...form, expiry_hours: +e.target.value })}
                  className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs">
            <option value={6}>⏱ 6h expiry</option>
            <option value={12}>⏱ 12h expiry</option>
            <option value={24}>⏱ 24h expiry</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs font-black text-amber-300 bg-amber-500/10 border border-amber-400/40 rounded-lg px-2 py-1.5">
          <input type="checkbox" checked={form.is_pinned_urgent} onChange={(e) => setForm({ ...form, is_pinned_urgent: e.target.checked })} data-testid="atc-post-urgent" />
          ⚡ Pin as URGENT ({settings?.urgent_pin_cost_at || 20} AT, top of feed for {settings?.urgent_pin_hours || 2}h)
        </label>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded-xl bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="atc-post-submit"
                  className="flex-1 px-3 py-2 rounded-xl bg-emerald-500 text-slate-900 text-sm font-black disabled:opacity-50">
            {busy ? '⏳' : `✅ Post & Lock ${holdAmt} AT`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Detail Modal ============
function DemandDetailModal({ demandId, me, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [sellerStats, setSellerStats] = useState({}); // seller_id → {rating_avg, rating_count, is_top_rated}
  const [showBid, setShowBid] = useState(false);
  const [showOTP, setShowOTP] = useState(null);
  const [showThread, setShowThread] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/atc/demands/${demandId}`); setData(r.data); }
    catch (e) { toast.error('Load failed'); onClose(); }
  }, [demandId, onClose]);
  useEffect(() => { load(); }, [load]);

  // Batch-fetch seller ratings for all merchants who bid on this demand
  useEffect(() => {
    if (!data) return;
    const ids = Array.from(new Set((data.bids || []).map((b) => b.merchant_id).filter(Boolean)));
    if (ids.length === 0) return;
    api.post('/atc/sellers/stats-batch', { seller_ids: ids })
      .then((r) => setSellerStats(r.data.stats || {}))
      .catch(() => {});
  }, [data]);

  if (!data) return null;
  const { demand, bids } = data;
  const isOwner = me?.id === demand.buyer_id;
  const acceptedBid = bids.find((b) => b.status === 'ACCEPTED');
  const isB2B = demand.deal_type === 'B2B_BULK';

  const accept = async (bid) => {
    if (!window.confirm(`Accept bid of ${bid.offered_price_at} AT from @${bid.merchant_username}? Full escrow will lock.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/atc/demands/${demandId}/accept-bid`, { bid_id: bid.id });
      setShowOTP({ order_id: r.data.order.order_id, otp: r.data.otp });
      toast.success(`🔒 Deal locked! Share OTP ${r.data.otp} with merchant.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const cancelDemand = async () => {
    if (!window.confirm('Cancel this demand? Commitment AT will be refunded (urgent fee not refundable).')) return;
    try {
      const r = await api.post(`/atc/demands/${demandId}/cancel`, { reason: 'buyer_cancel' });
      toast.success(`✅ Cancelled. ${r.data.refund_at} AT refunded.`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="atc-detail-modal"
           className="w-full max-w-lg rounded-2xl bg-slate-900 border-2 border-purple-400/60 p-4 max-h-[92vh] overflow-y-auto space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-lg font-black">{demand.title}</div>
            <div className="text-[10px] opacity-70">By @{demand.buyer_username} · {demand.city || 'Anywhere'} · Status: <b className="text-emerald-300">{demand.status}</b></div>
          </div>
          <button onClick={onClose} className="px-2 py-0.5 rounded bg-slate-700 text-xs">✕</button>
        </div>
        {demand.description && <div className="text-xs opacity-90">{demand.description}</div>}
        {demand.media_urls && demand.media_urls.length > 0 && (
          <div className="flex gap-1.5 flex-wrap" data-testid="atc-detail-media">
            {demand.media_urls.map((u, i) => {
              const isVideo = /\.(mp4|mov|webm|avi)/i.test(String(u));
              return (
                <button key={u} onClick={() => setLightbox({ items: normalizeItems(demand.media_urls), index: i })}
                        data-testid={`atc-detail-media-open-${i}`}
                        className="relative w-24 h-24 rounded border border-slate-700 overflow-hidden hover:border-emerald-400 transition group">
                  {isVideo ? (
                    <>
                      <video src={u} className="w-full h-full object-cover" muted />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-2xl font-black">▶</div>
                    </>
                  ) : (
                    <img src={u} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />
                  )}
                  <span className="absolute bottom-0.5 right-0.5 text-[8px] font-black bg-black/60 text-white px-1 rounded">🔍</span>
                </button>
              );
            })}
          </div>
        )}
        {demand.audio_note_url && (
          <button onClick={() => new Audio(demand.audio_note_url).play()} data-testid="atc-demand-play-audio"
                  className="px-2 py-1 rounded bg-cyan-500/20 text-cyan-300 text-[10px] font-black">🔁 Play buyer's audio note</button>
        )}
        <div className="flex items-center gap-2 flex-wrap text-[10px]">
          <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-black">💰 Budget {demand.target_budget_at} AT</span>
          <span className="px-1.5 py-0.5 rounded bg-slate-800 opacity-80">📦 Qty {demand.quantity}</span>
          <span className="px-1.5 py-0.5 rounded bg-slate-800 opacity-80">🚚 {demand.fulfillment_mode === 'RIDER' ? 'Rider' : 'Counter'}</span>
          <span className="px-1.5 py-0.5 rounded bg-slate-800 opacity-80">⏱ {demand.expiry_hours}h</span>
          {isB2B && demand.sample_required && <span className="px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-200 font-black">🧪 Sample-first · Two-stage escrow</span>}
        </div>

        <ShareButtons demand={demand} />

        {/* 2-way voice chat thread on this demand */}
        <button onClick={() => setShowThread(true)} data-testid="atc-detail-voice-thread"
                className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-cyan-400/40 hover:bg-slate-700 text-cyan-300 text-xs font-black flex items-center justify-center gap-1.5">
          💬 Open Voice Chat Thread — 2-way baat-cheet without placing a bid
        </button>

        <div className="pt-2 border-t border-slate-700">
          <div className="text-xs font-black mb-1">💬 {bids.length} bid{bids.length !== 1 ? 's' : ''}</div>
          <div className="space-y-1.5">
            {bids.length === 0 && <div className="text-[10px] opacity-60">No bids yet.</div>}
            {bids.map((b) => (
              <div key={b.id} data-testid={`atc-bid-${b.id}`}
                   className={`rounded-lg p-2 border ${b.status === 'ACCEPTED' ? 'bg-emerald-500/15 border-emerald-400' : b.status === 'REJECTED' ? 'bg-slate-800 border-slate-700 opacity-50' : 'bg-slate-800/60 border-slate-700'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs min-w-0 flex-1">
                    <span className="font-black text-emerald-300">{b.offered_price_at} AT</span>
                    <span className="opacity-70 ml-1">by @{b.merchant_username}</span>
                    <SellerRatingBadge stats={sellerStats[b.merchant_id]} />
                    {b.ready_in_minutes && <span className="text-[10px] opacity-60 ml-1">· ready in {b.ready_in_minutes} min</span>}
                  </div>
                  {b.merchant_id && (
                    <a href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/atc/merchant/${b.merchant_id}/poster.html`}
                       target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                       title={`Print @${b.merchant_username}'s A4 bid poster`}
                       data-testid={`atc-bid-poster-${b.id}`}
                       className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-black hover:bg-amber-500/30">
                      🖨️
                    </a>
                  )}
                  {isOwner && b.status === 'PENDING' && demand.status === 'OPEN' && (
                    <button onClick={() => accept(b)} disabled={busy} data-testid={`atc-accept-${b.id}`}
                            className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">Accept →</button>
                  )}
                  {b.status === 'ACCEPTED' && <span className="text-[10px] font-black text-emerald-300">✅ ACCEPTED</span>}
                </div>
                {b.note && <div className="text-[10px] opacity-70 mt-0.5">{b.note}</div>}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {b.audio_pitch_url && (
                    <button onClick={() => new Audio(b.audio_pitch_url).play()} data-testid={`atc-bid-play-${b.id}`}
                            className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 text-[9px] font-black">🔁 Play pitch</button>
                  )}
                  {b.sample_first_ack && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-200 font-black">🧪 Sample-first ✓</span>}
                  {b.transport_bilty_url && (
                    <a href={b.transport_bilty_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                       data-testid={`atc-bid-bilty-${b.id}`}
                       className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-black">📄 Bilty</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-slate-700 flex gap-2 flex-wrap">
          {!isOwner && demand.status === 'OPEN' && (
            <button onClick={() => setShowBid(true)} data-testid="atc-open-bid"
                    className="flex-1 min-w-[140px] px-3 py-2 rounded-xl bg-emerald-500 text-slate-900 text-sm font-black">🎯 Place Bid</button>
          )}
          {isOwner && demand.status === 'OPEN' && (
            <button onClick={cancelDemand} className="flex-1 min-w-[140px] px-3 py-2 rounded-xl bg-rose-500 text-white text-sm font-black">Cancel Demand</button>
          )}
          {acceptedBid && demand.status === 'LOCKED' && (
            <div className="w-full rounded-lg bg-amber-500/20 border border-amber-400 p-2 text-xs space-y-1.5">
              🔒 Escrow locked. {isOwner ? 'Share the OTP with the merchant on delivery.' : `You are the winning merchant. Ask buyer for the OTP to unlock ${acceptedBid.offered_price_at} AT.`}
              {demand.order_id && (
                <OrderStagePanel orderId={demand.order_id} me={me} isOwner={isOwner} onDone={onChanged} />
              )}
            </div>
          )}
          {/* Post-deal actions for buyer: rate seller + raise dispute (within 24h) */}
          {isOwner && demand.status === 'COMPLETED' && demand.order_id && (
            <div className="w-full mt-2">
              <PostDealActions orderId={demand.order_id} onChanged={onChanged} />
            </div>
          )}
        </div>
      </div>

      {showBid && (
        <PlaceBidModal demandId={demandId} demand={demand} onClose={() => setShowBid(false)}
                       onPlaced={() => { setShowBid(false); load(); }} />
      )}
      {showThread && (
        <VoiceThreadModal demand={demand} onClose={() => setShowThread(false)} />
      )}
      {lightbox && (
        <MediaLightbox items={lightbox.items} startIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
      {showOTP && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowOTP(null)}>
          <div onClick={(e) => e.stopPropagation()} className="rounded-2xl bg-slate-900 border-2 border-amber-400 p-5 text-center max-w-sm" data-testid="atc-otp-modal">
            <div className="text-lg font-black text-amber-300">🔐 Handshake OTP</div>
            <div className="text-4xl font-black tracking-widest text-white my-3">{showOTP.otp}</div>
            <div className="text-[11px] opacity-80">Share this 4-digit code with the merchant on delivery. Merchant enters this OTP to unlock the escrow payout.</div>
            <button onClick={() => setShowOTP(null)} className="mt-3 px-4 py-2 rounded-xl bg-amber-400 text-slate-900 text-sm font-black">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Place Bid Modal (with audio pitch + B2B bilty) ============
function PlaceBidModal({ demandId, demand, autoRecord, onClose, onPlaced }) {
  const isB2B = demand.deal_type === 'B2B_BULK';
  const [form, setForm] = useState({
    offered_price_at: autoRecord ? Number(demand.target_budget_at) : 0,
    ready_in_minutes: 60, note: '',
    audio_pitch_url: null, sample_first_ack: false, transport_bilty_url: null,
  });
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const bilFileRef = useRef(null);

  // Record 10-second audio pitch via MediaRecorder → upload
  const toggleRecord = async () => {
    if (recording) { mediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setUploading('audio');
        try {
          const fd = new FormData(); fd.append('file', blob, 'pitch.webm');
          const r = await api.post('/webshop/upload/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setForm((p) => ({ ...p, audio_pitch_url: r.data.url }));
          toast.success('🎤 Pitch recorded');
        } catch (e) { toast.error('Audio upload failed'); }
        finally { setUploading(null); }
      };
      mediaRef.current = mr;
      mr.start(); setRecording(true);
      setTimeout(() => { try { mr.state === 'recording' && mr.stop(); } catch (e) {} }, 10000);
    } catch (e) { toast.error('Mic access denied'); }
  };

  const uploadBilty = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading('bilty');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((p) => ({ ...p, transport_bilty_url: r.data.url }));
      toast.success('📄 Bilty uploaded');
    } catch (err) { toast.error('Bilty upload failed'); }
    finally { setUploading(null); if (bilFileRef.current) bilFileRef.current.value = ''; }
  };

  const submit = async () => {
    if (form.offered_price_at <= 0) { toast.error('Enter a valid bid'); return; }
    if (isB2B && demand.sample_required && !form.sample_first_ack) {
      toast.error('You must acknowledge sample-first for this B2B demand'); return;
    }
    setBusy(true);
    try { await api.post(`/atc/demands/${demandId}/bid`, form); toast.success('✅ Bid placed'); onPlaced(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/80 flex items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="atc-bid-modal"
           className="w-full max-w-sm rounded-2xl bg-slate-900 border-2 border-emerald-400/60 p-4 space-y-2">
        <div className="text-lg font-black">🎯 Place Bid {isB2B && <span className="text-[10px] text-purple-300">· B2B</span>}</div>
        <input type="number" min={1} value={form.offered_price_at || ''} onChange={(e) => setForm({ ...form, offered_price_at: +e.target.value })}
               placeholder="Your offered price (AT) *" data-testid="atc-bid-price" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input type="number" min={5} value={form.ready_in_minutes} onChange={(e) => setForm({ ...form, ready_in_minutes: +e.target.value })}
               placeholder="Ready in (minutes)" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
                  placeholder="Quality/notes for buyer" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />

        {/* Audio pitch */}
        <div className="flex items-center gap-2">
          <button onClick={toggleRecord} disabled={uploading === 'audio'} data-testid="atc-bid-record"
                  className={`px-3 py-1.5 rounded text-xs font-black ${recording ? 'bg-rose-500 animate-pulse text-white' : form.audio_pitch_url ? 'bg-emerald-500 text-slate-900' : 'bg-cyan-500 text-slate-900'}`}>
            {recording ? '⏹ Stop' : form.audio_pitch_url ? '🎤 Re-record' : '🎤 Record 10s pitch'}
          </button>
          {form.audio_pitch_url && (
            <button onClick={() => new Audio(form.audio_pitch_url).play()}
                    className="px-2 py-1 rounded bg-slate-700 text-cyan-300 text-[10px] font-black">🔁 Preview</button>
          )}
          {uploading === 'audio' && <span className="text-[10px] opacity-70">⏳ Uploading…</span>}
        </div>

        {isB2B && (
          <>
            {demand.sample_required && (
              <label className="flex items-center gap-2 text-xs font-black text-purple-300 bg-purple-500/10 border border-purple-400/40 rounded-lg px-2 py-1.5">
                <input type="checkbox" checked={form.sample_first_ack} onChange={(e) => setForm({ ...form, sample_first_ack: e.target.checked })} data-testid="atc-bid-sample-ack" />
                🧪 I will send a sample first before shipping bulk
              </label>
            )}
            <div className="flex items-center gap-2">
              <button onClick={() => bilFileRef.current?.click()} disabled={uploading === 'bilty'} data-testid="atc-bid-bilty-upload"
                      className={`px-3 py-1.5 rounded text-xs font-black ${form.transport_bilty_url ? 'bg-emerald-500 text-slate-900' : 'bg-amber-500 text-slate-900'}`}>
                {uploading === 'bilty' ? '⏳ Uploading…' : form.transport_bilty_url ? '📄 Bilty ✓ (change)' : '📄 Upload Transport Bilty (PDF)'}
              </button>
              <input ref={bilFileRef} type="file" accept="application/pdf,image/*" onChange={uploadBilty} className="hidden" />
            </div>
          </>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded-xl bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="atc-bid-submit"
                  className="flex-1 px-3 py-2 rounded-xl bg-emerald-500 text-slate-900 text-sm font-black disabled:opacity-50">
            {busy ? '⏳' : '✅ Place Bid'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Merchant OTP verify ============
function MerchantOTPForm({ orderId, onDone }) {
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!otp || otp.length !== 4) { toast.error('Enter 4-digit OTP'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/atc/orders/${orderId}/verify-otp`, { otp });
      toast.success(`🎉 Deal complete! ${r.data.commission_split.total_commission_collected} AT commission split.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'OTP failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex items-center gap-2">
      <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
             placeholder="OTP" data-testid="atc-otp-input"
             className="w-24 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-center font-black tracking-widest" />
      <button onClick={submit} disabled={busy} data-testid="atc-otp-submit"
              className="px-3 py-1.5 rounded bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-50">
        {busy ? '⏳' : 'Unlock Payout'}
      </button>
    </div>
  );
}

// ============ Share & Earn buttons (WhatsApp/Facebook/Instagram/Copy) ============
function ShareButtons({ demand }) {
  // Support fix 2026-09-24: share via the site's own domain + public post page (was: emergent.host backend
  // URL that landed on a login-only page), and add Copy link / Download image.
  const { user } = useAuth() || {};
  const origin = window.location.origin;
  const refQs = user?.username ? `?ref=${encodeURIComponent(user.username)}` : '';
  const shareUrl = `${origin}/api/atc/demand/${demand.id}/share${refQs}`;
  const text = `📢 ${demand.deal_type === 'B2B_BULK' ? 'B2B Wholesale' : 'B2C Retail'} demand on Anant Trade Center:\n\n"${demand.title}"\n💰 Budget ${demand.target_budget_at} AT · 📦 Qty ${demand.quantity}${demand.city ? ` · 📍 ${demand.city}` : ''}\n\nBid here${user?.username ? ' (earn 1% AT if it locks via your link)' : ''}:`;
  return (
    <div>
      <ShareBar shareUrl={shareUrl} text={text} title={demand.title} label="Share & Earn 1%:"
                imageUrl={`${origin}/api/atc/demand/${demand.id}/og.png`} testPrefix="atc-share" />
      {user?.username && (
        <span className="text-[9px] text-amber-300 opacity-80">🎁 Your ref link auto-added</span>
      )}
    </div>
  );
}

// ============ Order stage panel — handles both single-stage OTP and two-stage sample-approve ============
function OrderStagePanel({ orderId, me, isOwner, onDone }) {
  const [order, setOrder] = useState(null);
  const load = useCallback(async () => {
    try {
      // Reuse the demand endpoint's related bids — but we need the order directly.
      // Cheapest: call /atc/demands/{demand_id} to get order — but we only have order_id.
      // For MVP, we store enough on demand + bid; fetch bids and check accepted. Since we don't
      // have a GET /atc/orders/{id} endpoint, we optimistically stream via SSE or reload demand.
      // Simpler: expose enough state via demand.order_id + hidden fetch.
      const r = await api.get(`/atc/order/${orderId}`);
      setOrder(r.data.order);
    } catch (e) { /* endpoint may not exist yet */ }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  if (!order) return me?.id ? <MerchantOTPForm orderId={orderId} onDone={onDone} /> : null;

  const isSeller = me?.id === order.seller_id;
  const isBuyer = me?.id === order.buyer_id;

  // Two-stage flow
  if (order.two_stage) {
    if (order.stage === 'SAMPLE_PENDING') {
      return (
        <div className="mt-2 rounded bg-purple-500/15 border border-purple-400/40 p-2 text-[11px] space-y-1">
          <div><b>🧪 Stage 1 · Sample delivery pending.</b> Sample-lock: {order.sample_locked_at} AT (50%).</div>
          {isSeller && <div className="text-[10px] opacity-80">Send the physical sample to the buyer. Sample OTP has been shared with you in the previous popup — paste it inside the parcel.</div>}
          {isBuyer && <BuyerSampleApproveForm orderId={orderId} onDone={() => { load(); onDone && onDone(); }} />}
        </div>
      );
    }
    // FULL_LOCKED — normal delivery OTP flow
    return (
      <div className="mt-2 rounded bg-emerald-500/15 border border-emerald-400/40 p-2 text-[11px] space-y-1">
        <div><b>✅ Stage 2 · Bulk locked.</b> Full escrow: {order.total_amount_at} AT. New delivery OTP has been generated.</div>
        {isSeller && <MerchantOTPForm orderId={orderId} onDone={onDone} />}
      </div>
    );
  }

  // Single-stage — same as before
  return isSeller ? <MerchantOTPForm orderId={orderId} onDone={onDone} /> : null;
}

function BuyerSampleApproveForm({ orderId, onDone }) {
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (otp.length !== 4) { toast.error('Enter 4-digit sample OTP'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/atc/orders/${orderId}/approve-sample`, { sample_otp: otp });
      toast.success(`✅ Sample approved. ${r.data.bulk_locked_at} AT locked. Delivery OTP: ${r.data.delivery_otp}`);
      alert(`🔐 New Delivery OTP: ${r.data.delivery_otp}\n\nShare this with the merchant when the bulk shipment arrives.`);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] opacity-80">Enter the sample OTP the seller sent with the parcel:</span>
      <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
             placeholder="Sample OTP" data-testid="atc-sample-otp-input"
             className="w-24 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-center font-black tracking-widest" />
      <button onClick={submit} disabled={busy} data-testid="atc-sample-otp-submit"
              className="px-3 py-1.5 rounded bg-purple-500 text-white text-xs font-black disabled:opacity-50">
        {busy ? '⏳' : 'Approve sample · lock 2nd half'}
      </button>
    </div>
  );
}

// ============ Post-deal buyer actions: 1-tap rating + dispute (24h window) ============
function PostDealActions({ orderId, onChanged }) {
  const [ratingDone, setRatingDone] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [busy, setBusy] = useState(false);

  const rate = async (stars) => {
    setBusy(true);
    try {
      await api.post(`/atc/orders/${orderId}/rate`, { stars });
      setRatingDone(true); toast.success(`⭐ Rated ${stars}★ — thanks!`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rating failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2 space-y-1.5 text-xs" data-testid="atc-post-deal-actions">
      {!ratingDone && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-black text-yellow-300">⭐ Rate this seller:</span>
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} onClick={() => rate(s)} disabled={busy}
                    data-testid={`atc-rate-${s}`}
                    className="px-2 py-1 rounded bg-slate-700 hover:bg-yellow-400 hover:text-slate-900 text-sm">
              {'⭐'.repeat(s)}
            </button>
          ))}
        </div>
      )}
      {ratingDone && <div className="text-emerald-300 font-black">✅ Rated. Thanks!</div>}
      <button onClick={() => setShowDispute(true)} data-testid="atc-open-dispute"
              className="w-full px-2 py-1.5 rounded bg-rose-500/20 border border-rose-400/60 text-rose-200 text-[11px] font-black hover:bg-rose-500/30">
        🚨 Report issue (within 24h) — missing / damaged / not as described
      </button>
      {showDispute && <DisputeModal orderId={orderId} onClose={() => setShowDispute(false)} onRaised={() => { setShowDispute(false); onChanged && onChanged(); }} />}
    </div>
  );
}

function DisputeModal({ orderId, onClose, onRaised }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 8) { toast.error('Please describe the issue (8+ chars)'); return; }
    setBusy(true);
    try {
      await api.post(`/atc/orders/${orderId}/dispute`, { reason: reason.trim(), evidence_urls: [] });
      toast.success('🚨 Dispute raised — admin will review shortly');
      onRaised();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="atc-dispute-modal"
           className="w-full max-w-sm rounded-2xl bg-slate-900 border-2 border-rose-400/60 p-4 space-y-2">
        <div className="text-lg font-black text-rose-300">🚨 Raise Dispute</div>
        <div className="text-[10px] opacity-80">Order is frozen until admin resolves (refund / release / dismiss). 24h window applies.</div>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
                  placeholder="Describe what went wrong (damage, missing items, wrong product, etc.)"
                  data-testid="atc-dispute-reason"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded-xl bg-slate-700 text-xs font-black">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="atc-dispute-submit"
                  className="flex-1 px-3 py-2 rounded-xl bg-rose-500 text-white text-sm font-black disabled:opacity-50">
            {busy ? '⏳' : '🚨 Submit Dispute'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Seller rating badge (Wave 23) ============
function SellerRatingBadge({ stats }) {
  if (!stats) return null;
  const tierMeta = {
    platinum: { bg: 'from-slate-200 to-slate-400', text: '💎 PLATINUM' },
    gold:     { bg: 'from-yellow-400 to-amber-600', text: '🥇 GOLD' },
    silver:   { bg: 'from-slate-300 to-slate-500', text: '🥈 SILVER' },
  }[stats.tier];
  return (
    <span className="inline-flex items-center gap-1 ml-1">
      {stats.rating_count > 0 && (
        stats.is_top_rated ? (
          <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-[9px] font-black"
                title={`${stats.rating_avg}★ · ${stats.rating_count} reviews`} data-testid="atc-top-rated-badge">
            ⭐ {stats.rating_avg} · {stats.rating_count} · TOP RATED
          </span>
        ) : (
          <span className="px-1 py-0.5 rounded bg-slate-700 text-yellow-300 text-[9px] font-black"
                title={`${stats.rating_avg}★ · ${stats.rating_count} reviews`} data-testid="atc-rating-badge">
            ⭐ {stats.rating_avg} <span className="opacity-70">({stats.rating_count})</span>
          </span>
        )
      )}
      {tierMeta && (
        <span className={`px-1.5 py-0.5 rounded-full bg-gradient-to-r ${tierMeta.bg} text-slate-900 text-[9px] font-black`}
              title={`${stats.xp} XP · ${stats.tier} vendor`} data-testid={`atc-tier-${stats.tier}`}>
          {tierMeta.text}
        </span>
      )}
    </span>
  );
}

// ============ King of the Week badge (Wave 23) ============
function KingBadge() {
  const [king, setKing] = useState(null);
  useEffect(() => {
    api.get('/atc/leaderboard/king').then((r) => setKing(r.data?.king_of_the_week ? r.data : null)).catch(() => {});
  }, []);
  if (!king || !king.top || !king.top.length) return null;
  const first = king.top[0];
  return (
    <span data-testid="atc-king-badge"
          className="px-2 py-1 rounded-full text-[10px] font-black bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-500 text-slate-900 border border-yellow-200 shadow-lg shadow-amber-500/40 animate-pulse">
      🥇 King of the Week: @{first.username}
    </span>
  );
}

// ============ Notifications bell + drawer (Wave 23) ============
function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/atc/notifications', { params: { limit: 40 } });
      setItems(r.data.notifications || []); setUnread(r.data.unread || 0);
    } catch (e) { /* silent — user might not be logged in */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const openDrawer = async () => {
    setOpen(true);
    // Mark all as read on open
    try { await api.post('/atc/notifications/mark-read', {}); setUnread(0); }
    catch (e) { /* noop */ }
  };

  const iconFor = (kind) => ({
    bid_received: '💬', deal_locked: '🔒', dispute_filed: '🚨',
    rating_received: '⭐', leaderboard: '🥇',
  }[kind] || '🔔');

  return (
    <>
      <button onClick={openDrawer} data-testid="atc-notif-bell"
              className="relative px-2.5 py-1.5 rounded-full bg-slate-800 border border-slate-600 text-white text-xs font-black hover:bg-slate-700">
        🔔
        {unread > 0 && (
          <span data-testid="atc-notif-bell-badge"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-black flex items-center justify-center animate-pulse">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-[55] bg-black/70 flex justify-end" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="atc-notif-drawer"
               className="w-full max-w-sm h-full bg-slate-900 border-l-2 border-emerald-400/40 p-3 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-black">🔔 ATC Notifications</div>
              <button onClick={() => setOpen(false)} className="px-2 py-0.5 rounded bg-slate-700 text-xs">✕</button>
            </div>
            <NotifPrefsPanel />
            {items.length === 0 && <div className="text-xs opacity-60 py-6 text-center">No notifications yet.</div>}
            <div className="space-y-1.5">
              {items.map((n) => (
                <div key={n.id} data-testid={`atc-notif-item-${n.kind}`}
                     className={`rounded-lg border p-2 ${n.read ? 'bg-slate-800/40 border-slate-700' : 'bg-emerald-500/10 border-emerald-400/40'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{iconFor(n.kind)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black">{n.title}</div>
                      <div className="text-[11px] opacity-80">{n.body}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============ Notification preferences (Wave 24) ============
function NotifPrefsPanel() {
  const [prefs, setPrefs] = useState(null);
  const [open, setOpen] = useState(false);
  const KINDS = [
    { key: 'bid_received',    label: '💬 Bid received' },
    { key: 'deal_locked',     label: '🔒 Deal locked' },
    { key: 'dispute_filed',   label: '🚨 Dispute filed' },
    { key: 'rating_received', label: '⭐ Rating received' },
    { key: 'leaderboard',     label: '🥇 Leaderboard' },
  ];
  useEffect(() => { api.get('/atc/notifications/prefs').then((r) => setPrefs(r.data.prefs)).catch(() => {}); }, []);
  const toggle = async (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    try { await api.post('/atc/notifications/prefs', { [k]: next[k] }); toast.success('✅ Prefs updated'); }
    catch (e) { toast.error('Failed'); }
  };
  if (!prefs) return null;
  return (
    <div className="mb-3 rounded-lg border border-slate-700 bg-slate-800/60" data-testid="atc-notif-prefs">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] font-black">
        <span>⚙️ Notification Preferences</span>
        <span className="opacity-60">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1">
          {KINDS.map((k) => (
            <label key={k.key} className="flex items-center justify-between text-[11px] py-0.5" data-testid={`atc-pref-${k.key}`}>
              <span>{k.label}</span>
              <input type="checkbox" checked={!!prefs[k.key]} onChange={() => toggle(k.key)} className="accent-emerald-400" />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Merchant Bid Scorecard (Wave 24) ============
function MerchantScorecard() {
  const { user } = useAuth() || {};
  const [card, setCard] = useState(null);
  const [posterStats, setPosterStats] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/atc/merchant/${user.id}/scorecard`, { params: { days: 7 } })
       .then((r) => setCard(r.data))
       .catch(() => {});
    api.get(`/atc/merchant/${user.id}/poster/stats`, { params: { days: 30 } })
       .then((r) => setPosterStats(r.data))
       .catch(() => {});
  }, [user?.id]);
  if (!card || card.total_bids === 0) return null;
  const isKing = card.is_top_undercutter;
  const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const posterUrl = `${backend}/api/atc/merchant/${user.id}/poster.html`;
  return (
    <div className="max-w-5xl mx-auto mb-3 rounded-xl p-3 border-2 border-emerald-400/40 bg-gradient-to-r from-emerald-900/40 via-cyan-900/40 to-slate-900" data-testid="atc-scorecard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-black uppercase text-emerald-300">📊 Your Weekly Scorecard (7d)</div>
          {/* Wave 36 · trend sparkline (uses win_rate proxy scaled to 7-day bid history) */}
          {card.daily_wins && card.daily_wins.length > 0 && (
            <Sparkline data={card.daily_wins} width={70} height={16} color="#34d399" showArea />
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isKing && <span className="px-2 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[10px] font-black animate-pulse">🥇 #1 UNDERCUTTER</span>}
          <a href={posterUrl} target="_blank" rel="noopener noreferrer"
             data-testid="atc-scorecard-print-poster"
             className="px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[10px] font-black hover:scale-105 transition shadow shadow-amber-500/30">
            🖨️ Print A4 Poster
          </a>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
        <ScoreCell label="Total Bids" value={card.total_bids} />
        <ScoreCell label="Wins" value={card.wins} accent="emerald" />
        <ScoreCell label="Win-rate" value={`${card.win_rate_pct}%`} accent="cyan" />
        <ScoreCell label="Avg Bid" value={`${card.avg_bid_at} AT`} />
        <ScoreCell label="🥇 Lowest bids" value={card.lowest_bid_count} accent="amber" />
      </div>
      {card.top_undercutter && !isKing && (
        <div className="mt-2 text-[11px] rounded-lg p-2 bg-slate-900/60 border border-slate-700" data-testid="atc-scorecard-compare">
          🎯 To catch <b className="text-amber-300">@{card.top_undercutter.username}</b>{' '}
          ({card.top_undercutter.lowest_bids} lowest bids), you need <b>{card.gap_to_top_undercutter}</b> more lowest-bid wins this week.
        </div>
      )}
      {card.rating_count > 0 && (
        <div className="mt-1 text-[10px] opacity-80">⭐ Your rating: {card.rating_avg} · {card.rating_count} reviews</div>
      )}
      {posterStats && (posterStats.views + posterStats.scans + posterStats.conversions > 0) && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]" data-testid="atc-poster-stats">
          <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 font-black">
            🖨️ Poster · 30d
          </span>
          <span className="px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 font-black">
            👀 {posterStats.views} views
          </span>
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-200 font-black">
            📱 {posterStats.scans} QR scans
          </span>
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 font-black">
            💰 {posterStats.conversions} deals ({posterStats.conversion_rate_pct}%)
          </span>
        </div>
      )}
      <div className="mt-1.5 text-[9px] opacity-60">
        💡 <b>Print poster</b> aur shop pe QR ke saath tang do — customers scan karke live bid dekh sakte hain.
      </div>
    </div>
  );
}

function ScoreCell({ label, value, accent }) {
  const cls = {
    emerald: 'text-emerald-300', cyan: 'text-cyan-300', amber: 'text-amber-300',
  }[accent] || 'text-white';
  return (
    <div className="rounded-lg bg-slate-800/70 border border-slate-700 p-2">
      <div className="text-[9px] font-black uppercase opacity-70">{label}</div>
      <div className={`text-lg font-black ${cls}`}>{value}</div>
    </div>
  );
}


// ============ Voice Chat Thread Modal (Wave 26 — 2-way, Wave 27 — Whisper + ratings + AI) ============
function VoiceThreadModal({ demand, onClose }) {
  const { user } = useAuth() || {};
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [aiSuggest, setAiSuggest] = useState(null); // { suggestion, sample_size, ...}
  const [aiBusy, setAiBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const listEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/atc/demand/${demand.id}/voice-thread`);
      setMessages(r.data.messages || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [demand.id]);

  useEffect(() => { load(); }, [load]);

  // Live refresh — safety net alongside SSE
  useEffect(() => {
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (listEndRef.current) listEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const toggleRecord = async () => {
    if (recording) { mediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setUploading(true);
        try {
          const fd = new FormData(); fd.append('file', blob, 'voice-reply.webm');
          const r = await api.post('/webshop/upload/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          toast.info('🎤 Transcribing… please wait ~3s');
          await api.post(`/atc/demand/${demand.id}/voice-thread`, { audio_url: r.data.url, note: note.trim() || null, transcribe: true });
          toast.success('✅ Voice reply posted');
          setNote('');
          load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send voice reply'); }
        finally { setUploading(false); }
      };
      mediaRef.current = mr;
      mr.start(); setRecording(true);
      setTimeout(() => { try { mr.state === 'recording' && mr.stop(); } catch (e) {} }, 15000);
    } catch (e) { toast.error('Mic access denied'); }
  };

  const playMsg = (m) => {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      const a = new Audio(m.audio_url);
      audioRef.current = a;
      setPlayingId(m.id);
      a.onended = () => setPlayingId(null);
      a.onerror = () => setPlayingId(null);
      a.play();
    } catch (e) { toast.error('Playback failed'); setPlayingId(null); }
  };

  const stopPlay = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingId(null);
  };

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const rateMsg = async (m, stars) => {
    try {
      const r = await api.post(`/atc/voice-thread/${m.id}/rate`, { stars });
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, rating_avg: r.data.rating_avg, rating_count: r.data.rating_count, my_stars: stars } : x));
      if (r.data.xp_awarded > 0) toast.success(`⭐ +${r.data.xp_awarded} XP awarded to @${m.username}`);
      else toast.success(`⭐ Rated ${stars}★`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rating failed'); }
  };

  const fetchAiSuggest = async () => {
    setAiBusy(true);
    try {
      const r = await api.get(`/atc/demand/${demand.id}/rate-suggestion`);
      setAiSuggest(r.data);
    } catch (e) { toast.error('AI suggestion failed'); }
    finally { setAiBusy(false); }
  };

  const relTime = (iso) => {
    if (!iso) return '';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    if (diff < 60000) return 'just now';
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="atc-voice-thread-modal"
           className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-cyan-400/60 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="p-3 border-b border-slate-700 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-black text-cyan-300">💬 Voice Chat · {demand.title}</div>
            <div className="text-[10px] opacity-70">
              2-way baat-cheet · Whisper transcript · ⭐ ratings · max 15s per note
            </div>
          </div>
          <button onClick={onClose} data-testid="atc-voice-thread-close"
                  className="px-2 py-0.5 rounded bg-slate-700 text-xs">✕</button>
        </div>

        {/* AI Rate Suggestion */}
        <div className="px-3 py-2 border-b border-slate-800 bg-gradient-to-r from-fuchsia-950/50 to-cyan-950/50">
          {!aiSuggest && (
            <button onClick={fetchAiSuggest} disabled={aiBusy} data-testid="atc-ai-suggest-btn"
                    className="w-full px-3 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white text-[11px] font-black hover:scale-[1.01] transition disabled:opacity-50">
              {aiBusy ? '🧠 Analyzing…' : '🤖 Suggest Fair Rate (AI · 7d market data)'}
            </button>
          )}
          {aiSuggest && (
            <div className="text-[11px] rounded-lg p-2 bg-slate-900/70 border border-fuchsia-400/40 space-y-1" data-testid="atc-ai-suggest-panel">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="px-1.5 py-0.5 rounded-full bg-fuchsia-500/25 text-fuchsia-200 text-[9px] font-black">🤖 AI Rate Tip</span>
                {aiSuggest.sample_size > 0 && (
                  <span className="text-[9px] opacity-70">
                    based on {aiSuggest.sample_size} wins · avg {aiSuggest.avg_win} AT
                  </span>
                )}
                <button onClick={fetchAiSuggest} disabled={aiBusy}
                        data-testid="atc-ai-suggest-refresh"
                        className="ml-auto text-[9px] opacity-60 hover:opacity-100">↻ refresh</button>
              </div>
              <div className="text-white font-black">{aiSuggest.suggestion}</div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px]" data-testid="atc-voice-thread-list">
          {loading && <div className="text-[11px] opacity-60 text-center py-8">⏳ Loading…</div>}
          {!loading && messages.length === 0 && (
            <div className="text-[11px] opacity-60 text-center py-8">
              🎤 No voice replies yet — kick off the conversation!
            </div>
          )}
          {messages.map((m) => {
            const mine = user?.id === m.user_id;
            const isBuyer = m.role === 'buyer';
            return (
              <div key={m.id} data-testid={`atc-voice-msg-${m.id}`}
                   className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl p-2 space-y-1 ${
                    mine ? 'bg-emerald-500/20 border border-emerald-400/40 rounded-br-sm'
                         : 'bg-slate-800/80 border border-slate-700 rounded-bl-sm'
                  }`}>
                  <div className="flex items-center gap-1.5 text-[9px] font-black opacity-80">
                    <span className={isBuyer ? 'text-amber-300' : 'text-cyan-300'}>
                      {isBuyer ? '🛒 Buyer' : '🏪 Merchant'}
                    </span>
                    <span>@{m.username}</span>
                    {mine && <span className="opacity-60">(you)</span>}
                    <span className="ml-auto opacity-60">{relTime(m.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {playingId === m.id ? (
                      <button onClick={stopPlay} data-testid={`atc-voice-msg-stop-${m.id}`}
                              className="px-2 py-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center gap-1">
                        ⏸ Stop
                      </button>
                    ) : (
                      <button onClick={() => playMsg(m)} data-testid={`atc-voice-msg-play-${m.id}`}
                              className="px-2 py-1 rounded-full bg-cyan-500 text-slate-900 text-[10px] font-black flex items-center gap-1">
                        ▶ Play
                      </button>
                    )}
                    <div className="h-1.5 flex-1 rounded-full bg-slate-700 overflow-hidden">
                      <div className={`h-full ${playingId === m.id ? 'bg-cyan-400 animate-pulse w-full' : 'bg-slate-500 w-1/4'}`} />
                    </div>
                  </div>
                  {/* Whisper transcript (Wave 27) — public-space friendly */}
                  {m.transcript && (
                    <div className="text-[10.5px] leading-snug rounded bg-slate-900/60 border border-slate-700 px-2 py-1 mt-1" data-testid={`atc-voice-transcript-${m.id}`}>
                      <span className="text-[9px] font-black text-cyan-300 uppercase mr-1">📝 Transcript</span>
                      <span className="opacity-90">{m.transcript}</span>
                    </div>
                  )}
                  {m.note && <div className="text-[11px] opacity-90 whitespace-pre-wrap">{m.note}</div>}
                  {/* Ratings row */}
                  {!mine && (
                    <div className="flex items-center gap-0.5 mt-1" data-testid={`atc-voice-rate-${m.id}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} onClick={() => rateMsg(m, n)}
                                data-testid={`atc-voice-rate-${m.id}-${n}`}
                                title={`${n} star${n>1?'s':''}`}
                                className={`text-sm leading-none ${n <= (m.my_stars || 0) ? 'text-amber-300' : 'text-slate-600 hover:text-amber-200'}`}>
                          ★
                        </button>
                      ))}
                      {m.rating_count > 0 && (
                        <span className="ml-1 text-[9px] opacity-70">
                          {m.rating_avg} · {m.rating_count}
                        </span>
                      )}
                    </div>
                  )}
                  {mine && m.rating_count > 0 && (
                    <div className="text-[9px] opacity-70 flex items-center gap-1">
                      <span className="text-amber-300">★</span>
                      <span>{m.rating_avg} · {m.rating_count} rating{m.rating_count > 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={listEndRef} />
        </div>

        {/* Composer */}
        <div className="p-3 border-t border-slate-700 space-y-2 bg-slate-950/60">
          <input type="text" value={note} onChange={(e) => setNote(e.target.value.slice(0, 280))}
                 placeholder="Optional note (max 280 chars)..." data-testid="atc-voice-thread-note"
                 className="w-full px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-[11px]" />
          <div className="flex items-center gap-2">
            <button onClick={toggleRecord} disabled={uploading} data-testid="atc-voice-thread-record"
                    className={`flex-1 px-3 py-2 rounded-xl text-sm font-black transition ${
                      recording ? 'bg-rose-500 text-white animate-pulse' : 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-900 hover:scale-105'
                    } disabled:opacity-50`}>
              {uploading ? '⏳ Uploading + transcribing…' : recording ? '⏹ Stop & Send (15s max)' : '🎤 Hold & Speak — Send Voice Reply'}
            </button>
          </div>
          <div className="text-[9px] opacity-60 text-center">
            🔒 Recorded voice + Whisper transcript stored securely · no bid commitment
          </div>
        </div>
      </div>
    </div>
  );
}


// ============ Wave 37 · Live Party Share Modal (QR + WA + Copy) ============
function LivePartyShareModal({ url, qr, onClose }) {
  const [copied, setCopied] = useState(false);
  const waText = `🔴 LIVE Trading Party on Anant Trade Center — bids running now! Watch live: ${url}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(waText)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success('Link copied — paste anywhere'); setTimeout(() => setCopied(false), 2000); }
    catch (_) { toast.error('Copy failed'); }
  };
  const download = () => {
    if (!qr) return;
    const a = document.createElement('a');
    a.href = qr; a.download = 'anant-live-party-qr.png'; a.click();
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="atc-live-party-modal" onClick={onClose}>
      <div className="max-w-sm w-full rounded-2xl bg-gradient-to-br from-slate-900 via-fuchsia-950 to-slate-900 border-2 border-fuchsia-400/50 p-5 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-lg font-black text-fuchsia-200">🎥 Live Trading Party</div>
            <div className="text-[11px] opacity-75">Scan QR · Share link · Broadcast on kiosk</div>
          </div>
          <button onClick={onClose} data-testid="atc-live-party-close"
                  className="text-slate-300 hover:text-white text-xl leading-none px-1">×</button>
        </div>
        <div className="rounded-xl bg-white p-3 mx-auto w-fit shadow-lg mb-3" data-testid="atc-live-party-qr-wrap">
          {qr
            ? <img src={qr} alt="Live Party QR" className="w-64 h-64 block" data-testid="atc-live-party-qr-img" />
            : <div className="w-64 h-64 flex items-center justify-center text-slate-500 text-xs">Generating QR…</div>}
        </div>
        <div className="text-[10px] opacity-75 text-center mb-3 break-all font-mono">{url}</div>
        <div className="grid grid-cols-3 gap-2">
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="atc-live-party-wa"
             className="px-3 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-black text-center hover:brightness-110">
            💬 WhatsApp
          </a>
          <button onClick={copy} data-testid="atc-live-party-copy"
                  className="px-3 py-2 rounded-xl bg-slate-700 text-white text-xs font-black hover:brightness-110">
            {copied ? '✅ Copied' : '🔗 Copy Link'}
          </button>
          <button onClick={download} disabled={!qr} data-testid="atc-live-party-download"
                  className="px-3 py-2 rounded-xl bg-fuchsia-500 text-slate-900 text-xs font-black hover:brightness-110 disabled:opacity-40">
            ⬇ Save QR
          </button>
        </div>
        <div className="text-[10px] opacity-60 text-center mt-3">
          Kiosk tip: project this QR on your shop TV — customers scan & join live party 🎉
        </div>
      </div>
    </div>
  );
}


// ============ Wave 37 · One-time Onboarding Tour ============
const TOUR_STEPS = [
  { emoji: '👋', title: 'Anant Trade Center mein swagat hai!',
    body: 'Bharat ka apna live trading floor — B2C aur B2B dono. 4 quick steps, phir aap ready ho.' },
  { emoji: '🛒', title: 'Step 1 · B2C ya B2B chuno',
    body: 'Upar tabs mein switch karo — B2C retail (5% fee) ya B2B wholesale (3% fee, MOQ 50+).',
    target: '[data-testid="atc-tab-swap"]' },
  { emoji: '➕', title: 'Step 2 · Demand post karo',
    body: '"Post Demand" button dabao, kya chahiye + budget likho. Buyers ke liye, listing free hai.',
    target: '[data-testid="atc-post-cta"]' },
  { emoji: '🎤', title: 'Step 3 · Voice bid record karo',
    body: 'Kisi bhi demand pe hold-and-speak karke apni price aur delivery time record karo — 15 sec max.' },
  { emoji: '🔒', title: 'Step 4 · Deal lock ho jayegi',
    body: 'Buyer aapki bid accept kare → escrow lock → delivery ya counter pickup → auto-settle 24h mein.' },
  { emoji: '🚀', title: 'Aur ek cool cheez',
    body: 'Broadcast Mode (🎥 icon) dabao → shop TV pe project karo → QR share karke live party host karo!' },
];

function OnboardingTour({ onDone }) {
  const [step, setStep] = useState(0);
  const cur = TOUR_STEPS[step];
  const isLast = step >= TOUR_STEPS.length - 1;
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    if (!cur?.target) { setHighlight(null); return; }
    try {
      const el = document.querySelector(cur.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setHighlight({ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 });
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else setHighlight(null);
    } catch (_) { setHighlight(null); }
  }, [step, cur]);

  return (
    <div className="fixed inset-0 z-[130]" data-testid="atc-onboarding-tour">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onDone} />
      {highlight && (
        <div className="absolute rounded-xl border-2 border-cyan-300 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] pointer-events-none animate-pulse"
             style={{ top: highlight.top, left: highlight.left, width: highlight.width, height: highlight.height }}
             data-testid="atc-tour-highlight" />
      )}
      <div className="absolute inset-x-0 bottom-6 flex justify-center px-4">
        <div className="max-w-md w-full rounded-2xl bg-gradient-to-br from-slate-900 via-cyan-950 to-slate-900 border-2 border-cyan-400/50 p-5 shadow-2xl"
             onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300">
              Step {step + 1} / {TOUR_STEPS.length}
            </div>
            <button onClick={onDone} data-testid="atc-tour-skip"
                    className="text-[10px] font-black text-slate-400 hover:text-white underline">Skip</button>
          </div>
          <div className="text-3xl mb-1">{cur.emoji}</div>
          <div className="text-base font-black text-white mb-1">{cur.title}</div>
          <div className="text-xs opacity-85 mb-4 leading-relaxed">{cur.body}</div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 flex-1">
              {TOUR_STEPS.map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-cyan-400' : 'bg-slate-700'}`} />
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} data-testid="atc-tour-back"
                      className="px-3 py-1.5 rounded-lg bg-slate-700 text-xs font-black text-white hover:brightness-110">← Back</button>
            )}
            <button onClick={() => isLast ? onDone() : setStep(step + 1)}
                    data-testid={isLast ? 'atc-tour-done' : 'atc-tour-next'}
                    className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-cyan-400 to-emerald-400 text-slate-900 text-xs font-black hover:brightness-110">
              {isLast ? '🚀 Let\'s go!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
